import { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Users, HeartHandshake, Handshake, Megaphone, ArrowDown, ArrowRight } from "lucide-react";
import { scrollToId, scrollToIdWhenReady } from "../components/site/scroll";
import { trackEvent } from "../components/site/track";
import { useI18n } from "../i18n";

const howIcons = [Users, HeartHandshake, Handshake, Megaphone];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7 },
};

export default function ImpactSocial() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, []);

  const goContact = () => {
    trackEvent("impact_contact_click", { language: lang });
    navigate("/");
    setTimeout(() => scrollToIdWhenReady("contact"), 250);
  };

  return (
    <main className="pt-[72px]" data-testid="impact-page">
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div {...fadeUp}>
            <p className="font-heading text-gold text-sm tracking-[0.25em] uppercase mb-6">{t("impactHome.eyebrow")}</p>
            <h1 className="font-heading font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl max-w-4xl mb-8" data-testid="impact-hero-title">
              {t("impact.heroTitle")}
            </h1>
            <p className="text-[#c8d3dc] leading-relaxed text-base md:text-lg max-w-2xl mb-10">{t("impact.heroSubtitle")}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollToId("initiatives")}
                className="btn-gold inline-flex items-center justify-center gap-2 px-8 py-4 text-base"
                data-testid="impact-hero-cta-primary"
              >
                {t("impact.heroCta1")} <ArrowDown size={18} />
              </button>
              <button
                onClick={goContact}
                className="btn-ghost inline-flex items-center justify-center gap-2 px-8 py-4 text-base"
                data-testid="impact-hero-cta-secondary"
              >
                {t("impact.heroCta2")} <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 lg:py-32 border-t border-white/10" data-testid="impact-engagement">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.h2 {...fadeUp} className="lg:col-span-4 font-heading font-bold tracking-tighter text-3xl sm:text-4xl">
            {t("impact.engagementTitle")}
          </motion.h2>
          <motion.div {...fadeUp} className="lg:col-span-8 space-y-5">
            {t("impact.engagementParas").map((p, i) => (
              <p key={i} className="text-[#c8d3dc] leading-relaxed">{p}</p>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-24 lg:py-32 border-t border-white/10" data-testid="impact-how">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.h2 {...fadeUp} className="font-heading font-bold tracking-tighter text-3xl sm:text-4xl mb-14">
            {t("impact.howTitle")}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t("impact.howItems").map((item, i) => {
              const Icon = howIcons[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.55, delay: 0.08 * i }}
                  className="service-card p-8"
                  data-testid={`impact-how-card-${i}`}
                >
                  <Icon size={26} className="text-gold mb-5" strokeWidth={1.6} />
                  <h3 className="font-heading font-bold text-lg mb-3">{item.title}</h3>
                  <p className="text-[#8A9DB1] leading-relaxed text-sm">{item.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="initiatives" className="py-24 lg:py-32 border-t border-white/10" data-testid="impact-initiatives">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.h2 {...fadeUp} className="font-heading font-bold tracking-tighter text-3xl sm:text-4xl mb-14">
            {t("impact.initiativesTitle")}
          </motion.h2>
          <motion.div {...fadeUp} className="service-card p-10 lg:p-14 max-w-3xl">
            <h3 className="font-heading font-bold text-xl mb-4">{t("impact.initiativesCardTitle")}</h3>
            <p className="text-[#c8d3dc] leading-relaxed">{t("impact.initiativesCardText")}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 lg:py-32 border-t border-white/10" data-testid="impact-participate">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            {t("impact.participateItems").map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: 0.08 * i }}
                className="border border-white/10 p-8"
                data-testid={`impact-participate-card-${i}`}
              >
                <p className="font-heading text-gold text-sm tracking-[0.2em] uppercase mb-4">{`0${i + 1}`}</p>
                <h3 className="font-heading font-bold text-lg mb-3">{item.title}</h3>
                <p className="text-[#8A9DB1] leading-relaxed text-sm">{item.text}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp}>
            <button
              onClick={goContact}
              className="btn-gold inline-flex items-center gap-2 px-8 py-4 text-base"
              data-testid="impact-participate-cta"
            >
              {t("impact.heroCta2")} <ArrowRight size={18} />
            </button>
          </motion.div>
        </div>
      </section>

      <section className="py-16 border-t border-white/10" data-testid="impact-transparency">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="font-heading text-sm tracking-[0.18em] uppercase text-[#8A9DB1] mb-5">{t("impact.transparencyTitle")}</h2>
          <div className="space-y-4 max-w-3xl">
            {t("impact.transparencyParas").map((p, i) => (
              <p key={i} className="text-[#8A9DB1] leading-relaxed text-sm">{p}</p>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
